import { AnonymousPermitFormProps } from "../types/forms/Permits/PermitFormProps";
import {
    ShipRepairsPermitFormState_default,
    ShipRepairsPermitFormState_populateErrorMessages,
    ShipRepairsPermitFormState_toRequest
} from "../types/forms/Permits/ShipRepairsPermitFormState";
import { PermitType } from "../common/enums";
import { ShipRepairsPermit_toFormState } from "../types/permits/ShipRepairsPermit";
import usePortControlPermitForm from "./usePortControlPermitForm";
import { showDangerDialog, showPortControlRecaptchaErrorDialog } from "../common";

export default function useShipRepairsPermitForm(props: AnonymousPermitFormProps) {
    const baseFormManager = usePortControlPermitForm({
        ...props,
        defaultFormState: ShipRepairsPermitFormState_default,
        populateErrorMessages: ShipRepairsPermitFormState_populateErrorMessages,
        permitType: PermitType.ShipRepairsPermit,
        toFormState: ShipRepairsPermit_toFormState,
        toRequest: ShipRepairsPermitFormState_toRequest,
        disableDataFetch: props.isAnonymousPermit,
        clearErrorMessages: formState => ShipRepairsPermitFormState_populateErrorMessages(formState, {})
    });

    const { formState, isNewPermit, storedPermit } = baseFormManager;
    const { resources } = props;

    async function submitCreateAnonymousPermitHandlerAsync(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        const grecaptcha = (window as any).grecaptcha;
        if (!grecaptcha) {
            console.error("No grecaptcha found.");
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("RefreshPageAndTryAgain"));
            return;
        }

        if (!props.recaptchaPublicSiteKey) {
            console.error("No recaptcha public site key found.");
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("RefreshPageAndTryAgain"));
            return;
        }

        const recaptchaToken = await grecaptcha.enterprise.execute(props.recaptchaPublicSiteKey);
        const request = ShipRepairsPermitFormState_toRequest(formState);
        request.recaptchaToken = recaptchaToken;

        await baseFormManager.createHandlerAsync(request, {
            onSuccess: () => {
                window.location.href = "/Permits/AnonymousPermitConfirmation";
            },
            onError: (problemDetails) => {
                if (!problemDetails.errors) {
                    showDangerDialog(
                        props.resources.get("SomethingWentWrong"),
                        props.resources.get("RefreshPageAndTryAgain")
                    );
                    return;
                }

                const isRecaptchaError = Object.keys(problemDetails.errors).includes("RecaptchaToken");
                if (isRecaptchaError) {
                    showPortControlRecaptchaErrorDialog(resources);
                    return;
                }

                baseFormManager.handleValidationErrors(problemDetails);
            }
        });
    }

    const submitHandler =
        props.isAnonymousPermit && isNewPermit
            ? submitCreateAnonymousPermitHandlerAsync
            : baseFormManager.submitHandler;

    const showAnonymousUserEmail =
        (props.isAnonymousPermit && isNewPermit) || (!!storedPermit && storedPermit.isAnonymousPermit);

    const canUpdateWithNotification = !!storedPermit && storedPermit.canUpdateWithNotification;

    return {
        ...baseFormManager,
        showAnonymousUserEmail,
        canUpdateWithNotification,
        submitHandler
    };
}
